import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, BedDouble, Users, Calendar, Plus, X, Check } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import LotteryCodeInput from "@/components/LotteryCodeInput";
import GestionaleLongPress from "@/components/pos/GestionaleLongPress";
import ScontrinoGestureLabel from "@/components/pos/ScontrinoGestureLabel";
import PrecontoButton from "@/components/pos/PrecontoButton";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const diffDays = (a, b) => { return Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000)); };
const formatItDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

// Categorie servizi base
const EXTRAS = [
  { id: "colazione", label: "Colazione", price: 8 },
  { id: "parking", label: "Parcheggio", price: 5 },
  { id: "city_tax", label: "Tassa di soggiorno", price: 2, perPerson: true },
  { id: "late_checkout", label: "Late check-out", price: 15 },
];

export default function POSBnb({ mode = "bnb" }) {
  const [tenant, setTenant] = useState(null);
  const [rooms, setRooms] = useState([]); // dal listino prodotti
  const [activeRoom, setActiveRoom] = useState(null);
  const [checkIn, setCheckIn] = useState(todayISO());
  const [checkOut, setCheckOut] = useState(addDays(todayISO(), 1));
  const [guests, setGuests] = useState(2);
  const [customer, setCustomer] = useState({ name: "", document_number: "", phone: "", guest_id: "" });
  const [guestHits, setGuestHits] = useState([]);
  const [extras, setExtras] = useState([]); // array of extra ids
  const [showPay, setShowPay] = useState(false);
  const [last, setLast] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [lotteryCode, setLotteryCode] = useState("");
  const [precontoOrderId, setPrecontoOrderId] = useState(null);

  useEffect(() => {
    api.get("/tenant/me").then(r => setTenant(r.data));
    api.get("/hotel/rooms").then(r => {
      const hotelRooms = (Array.isArray(r.data) ? r.data : [])
        .filter(x => x.active !== false)
        .map(x => ({
          id: x.id,
          name: [x.number, x.name].filter(Boolean).join(" · ") || "Camera",
          price: Number(x.base_price_night) || 0,
          vat: 10,
        }));
      if (hotelRooms.length) {
        setRooms(hotelRooms);
        setActiveRoom(hotelRooms[0]);
        return;
      }
      api.get(`/products?mode=${mode}`).then(pr => {
        const list = (Array.isArray(pr.data) ? pr.data : []).filter(p => p.visible !== false);
        setRooms(list);
        if (list[0]) setActiveRoom(list[0]);
      }).catch(() => {});
    }).catch(() => {
      api.get(`/products?mode=${mode}`).then(pr => {
        const list = (Array.isArray(pr.data) ? pr.data : []).filter(p => p.visible !== false);
        setRooms(list);
        if (list[0]) setActiveRoom(list[0]);
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    const q = customer.name.trim();
    if (customer.guest_id || q.length < 2) {
      setGuestHits([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/hotel/guests", { params: { q } })
        .then((r) => setGuestHits(Array.isArray(r.data) ? r.data : []))
        .catch(() => setGuestHits([]));
    }, 200);
    return () => clearTimeout(t);
  }, [customer.name, customer.guest_id]);

  const nights = diffDays(checkIn, checkOut);
  const roomPrice = activeRoom ? activeRoom.price : 0;
  const roomTotal = roomPrice * nights;
  const extrasTotal = extras.reduce((s, exId) => {
    const ex = EXTRAS.find(e => e.id === exId);
    if (!ex) return s;
    return s + (ex.perPerson ? ex.price * guests * nights : ex.price);
  }, 0);
  const total = roomTotal + extrasTotal;

  const toggleExtra = (exId) => {
    if (extras.includes(exId)) setExtras((Array.isArray(extras) ? extras : []).filter(x => x !== exId));
    else setExtras([...extras, exId]);
  };

  const pickGuest = (g) => {
    setCustomer({
      name: `${g.first_name || ""} ${g.last_name || ""}`.replace(/\s+/g, " ").trim(),
      document_number: g.document_number || "",
      phone: g.phone || "",
      guest_id: g.id,
    });
    setGuestHits([]);
  };

  const rememberGuest = async (cust) => {
    const name = (cust.name || "").trim();
    if (!name) return cust;
    const parts = name.split(/\s+/);
    const body = { first_name: parts[0], last_name: parts.slice(1).join(" ") || parts[0], document_type: "cie" };
    if ((cust.document_number || "").trim()) body.document_number = cust.document_number.trim();
    if ((cust.phone || "").trim()) body.phone = cust.phone.trim();
    try {
      let id = cust.guest_id;
      if (!id) {
        const r = await api.get("/hotel/guests", { params: { q: name } });
        const list = Array.isArray(r.data) ? r.data : [];
        const doc = (cust.document_number || "").trim().toLowerCase();
        const hit = list.find((g) => {
          const full = `${g.first_name || ""} ${g.last_name || ""}`.replace(/\s+/g, " ").trim().toLowerCase();
          if (doc && String(g.document_number || "").trim().toLowerCase() === doc) return true;
          return full === name.toLowerCase();
        });
        id = hit?.id || "";
      }
      if (id) {
        await api.patch(`/hotel/guests/${id}`, body);
        return { ...cust, guest_id: id };
      }
      const created = await api.post("/hotel/guests", body);
      return { ...cust, guest_id: created.data?.id || "" };
    } catch {
      return cust;
    }
  };

  const blankCustomer = () => ({ name: "", document_number: "", phone: "", guest_id: "" });

  const close = async (method) => {
    if (!activeRoom) return toast.error("Seleziona una camera");
    if (!customer.name.trim()) return toast.error("Inserisci il nome del cliente");
    const filed = await rememberGuest(customer);
    const pos = await authorizeCardIfNeeded({ method, amount: total });
    if (!pos.ok) { toast.error(pos.message || "Pagamento carta non autorizzato"); return; }
    if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
    const offlineId = newOfflineId();
    let oid = precontoOrderId || null;
    try {
      const lines = [
        {
          product_id: activeRoom.id,
          name: `${activeRoom.name} × ${nights} notti`,
          qty: 1,
          price: roomTotal,
          vat: activeRoom.vat || 10,
        },
        ...(Array.isArray(extras) ? extras : []).map(exId => {
          const ex = EXTRAS.find(e => e.id === exId);
          const lineTotal = ex.perPerson ? ex.price * guests * nights : ex.price;
          return { name: ex.label, qty: 1, price: lineTotal, vat: 10 };
        }),
      ];
      const notes = `Cliente: ${filed.name} · Doc: ${filed.document_number || "—"} · Tel: ${filed.phone || "—"} · Ospiti: ${guests} · Check-in: ${checkIn} · Check-out: ${checkOut}`;
      if (precontoOrderId) {
        const upd = { lines, notes };
        if (lotteryCode) upd.lottery_code = lotteryCode;
        await api.patch(`/orders/${precontoOrderId}`, upd, SERVER_ATTEMPT);
        oid = precontoOrderId;
      } else {
        const r = await api.post("/orders", { mode, lines, payment_method: method, notes, lottery_code: lotteryCode || undefined, offline_id: offlineId }, SERVER_ATTEMPT);
        oid = r.data.id;
      }
      const c = await api.post(`/orders/${oid}/close-fiscal`, { payment_method: method }, SERVER_ATTEMPT);
      setLast(c.data); setShowPay(false); setShowScontrino(true);
      setExtras([]); setCustomer(blankCustomer()); setLotteryCode(""); setPrecontoOrderId(null);
      toast.success(`Scontrino #${c.data.receipt_number} · ${nights} notti`);
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: oid,
        mode, lines: buildLines(), payment_method: method, lottery_code: lotteryCode || null,
        notes: `Cliente: ${filed.name} · Ospiti: ${guests}`,
      });
      if (off.printed) {
        setLast(off.order); setShowPay(false); setShowScontrino(true);
        setExtras([]); setCustomer(blankCustomer()); setLotteryCode(""); setPrecontoOrderId(null);
        return;
      }
      if (!off.handled) toast.error("Errore");
    }
  };

  const buildLines = () => {
    if (!activeRoom) return [];
    return [
      { product_id: activeRoom.id, name: `${activeRoom.name} × ${nights} notti`, qty: 1, price: roomTotal, vat: activeRoom.vat || 10 },
      ...(Array.isArray(extras) ? extras : []).map(exId => {
        const ex = EXTRAS.find(e => e.id === exId);
        const lineTotal = ex.perPerson ? ex.price * guests * nights : ex.price;
        return { name: ex.label, qty: 1, price: lineTotal, vat: 10 };
      }),
    ];
  };
  const saveOrderForGestionale = async () => {
    if (!activeRoom) { toast.error("Seleziona camera"); return null; }
    const filed = await rememberGuest(customer);
    try {
      const notes = `[GESTIONALE] Cliente: ${filed.name || "—"} · Doc: ${filed.document_number || "—"} · Tel: ${filed.phone || "—"} · Ospiti: ${guests} · ${checkIn}→${checkOut}`;
      if (precontoOrderId) {
        const r = await api.patch(`/orders/${precontoOrderId}`, { lines: buildLines(), notes });
        return r.data;
      }
      const r = await api.post("/orders", { mode, lines: buildLines(), notes });
      return r.data;
    } catch { toast.error("Errore"); return null; }
  };
  const onGestionaleClosed = () => { setExtras([]); setCustomer(blankCustomer()); setLotteryCode(""); setPrecontoOrderId(null); };

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" data-testid="bnb-back" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <BedDouble className="w-5 h-5 text-[#0891B2]" />
        <div className="font-heading font-semibold tracking-tight">Velta One · Alloggi / Pensioni</div>
      </header>

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        <section className="col-span-8 flex flex-col overflow-hidden p-4 overflow-auto pos-scroll">
          {/* Date & ospiti */}
          <div className="bg-white border border-velta-border rounded-md p-4 mb-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2"><Calendar className="w-3 h-3 inline" /> Check-in</label>
                <input data-testid="bnb-checkin" type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)} className="w-full h-11 px-3 border border-velta-border rounded-md font-mono" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2"><Calendar className="w-3 h-3 inline" /> Check-out</label>
                <input data-testid="bnb-checkout" type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)} min={addDays(checkIn, 1)} className="w-full h-11 px-3 border border-velta-border rounded-md font-mono" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2"><Users className="w-3 h-3 inline" /> Ospiti</label>
                <div className="inline-flex items-center border border-velta-border rounded-md h-11 w-full justify-between px-2">
                  <button data-testid="bnb-guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-8 h-8">−</button>
                  <span data-testid="bnb-guests" className="font-mono font-bold text-xl">{guests}</span>
                  <button data-testid="bnb-guests-plus" onClick={() => setGuests(guests + 1)} className="w-8 h-8">+</button>
                </div>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 bg-velta-bg px-3 py-1.5 rounded-full text-xs font-mono">
              <span className="text-velta-mute">{formatItDate(checkIn)} → {formatItDate(checkOut)}</span>
              <span className="text-velta-ink font-bold">{nights} {nights === 1 ? "notte" : "notti"}</span>
            </div>
          </div>

          {/* Camere */}
          <div className="mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Seleziona camera</div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {(Array.isArray(rooms) ? rooms : []).map(r => (
                <button
                  key={r.id}
                  data-testid={`bnb-room-${r.id}`}
                  onClick={() => setActiveRoom(r)}
                  className={`relative bg-white border-2 rounded-md p-4 text-left btn-tactile transition ${activeRoom?.id === r.id ? "border-[#0891B2] bg-[#0891B2]/5" : "border-velta-border hover:border-velta-ink"}`}
                >
                  {activeRoom?.id === r.id && <div className="absolute top-2 right-2 w-6 h-6 bg-[#0891B2] text-white rounded-full flex items-center justify-center"><Check className="w-4 h-4" /></div>}
                  <BedDouble className="w-8 h-8 text-[#0891B2] mb-2" strokeWidth={1.5} />
                  <div className="font-medium text-sm">{r.name}</div>
                  <div className="font-mono font-bold text-lg">{formatEUR(r.price)}<span className="text-xs font-normal text-velta-mute">/notte</span></div>
                </button>
              ))}
              {rooms.length === 0 && <div className="col-span-full text-center text-velta-mute py-6 text-sm">Aggiungi le camere in <Link to="/dashboard/alberghiero?tab=rooms" className="text-velta-ink underline">Alloggi</Link>. I clienti stanno in <Link to="/dashboard/alberghiero?tab=guests" className="text-velta-ink underline">Clienti</Link>.</div>}
            </div>
          </div>

          {/* Cliente */}
          <div className="bg-white border border-velta-border rounded-md p-4 mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Cliente dell&apos;alloggio</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="relative">
                <input data-testid="bnb-customer-name" placeholder="Nome e cognome *" value={customer.name} onChange={e => { setCustomer({ ...customer, name: e.target.value, guest_id: "" }); }} className="h-10 w-full px-3 border border-velta-border rounded-md text-sm" />
                {guestHits.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 top-11 bg-white border border-velta-border rounded-md shadow max-h-40 overflow-auto">
                    {guestHits.map((g) => (
                      <button key={g.id} type="button" data-testid={`bnb-guest-${g.id}`} onClick={() => pickGuest(g)} className="w-full text-left px-3 py-2 text-sm hover:bg-velta-bg border-b border-velta-border last:border-0">
                        <div className="font-medium">{g.first_name} {g.last_name}</div>
                        <div className="text-xs text-velta-mute">{g.document_number || "senza documento"}{g.phone ? ` · ${g.phone}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input data-testid="bnb-doc" placeholder="Documento (CI/Passaporto)" value={customer.document_number} onChange={e => setCustomer({ ...customer, document_number: e.target.value })} className="h-10 px-3 border border-velta-border rounded-md text-sm font-mono" />
              <input data-testid="bnb-phone" placeholder="Telefono" value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} className="h-10 px-3 border border-velta-border rounded-md text-sm font-mono" />
            </div>
          </div>

          {/* Extras */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Servizi extra</div>
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(EXTRAS) ? EXTRAS : []).map(ex => {
                const active = extras.includes(ex.id);
                const ppLabel = ex.perPerson ? `${formatEUR(ex.price)}/persona × ${guests} × ${nights}` : formatEUR(ex.price);
                return (
                  <button
                    key={ex.id}
                    data-testid={`bnb-extra-${ex.id}`}
                    onClick={() => toggleExtra(ex.id)}
                    className={`h-10 px-3 rounded-full text-xs font-medium border ${active ? "bg-[#0891B2] text-white border-[#0891B2]" : "bg-white border-velta-border"}`}
                  >
                    {ex.label} {active && <Check className="w-3 h-3 inline ml-1" />} <span className="opacity-70">{ppLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="col-span-4 border-l border-velta-border bg-white flex flex-col overflow-hidden">
          <div className="p-4 border-b border-velta-border">
            <h2 className="font-heading font-semibold text-lg">Soggiorno</h2>
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-4">
            {activeRoom ? (
              <>
                <div className="bg-velta-bg rounded-md p-3 mb-3">
                  <div className="font-medium text-sm">{activeRoom.name}</div>
                  <div className="text-xs text-velta-mute mt-1">{formatEUR(activeRoom.price)}/notte × {nights}</div>
                  <div className="font-mono font-bold text-right mt-1">{formatEUR(roomTotal)}</div>
                </div>
                {(Array.isArray(extras) ? extras : []).map(exId => {
                  const ex = EXTRAS.find(e => e.id === exId);
                  if (!ex) return null;
                  const lineTotal = ex.perPerson ? ex.price * guests * nights : ex.price;
                  return (
                    <div key={exId} className="border border-velta-border rounded-md p-3 mb-2 flex justify-between text-sm">
                      <div>
                        <div className="font-medium">{ex.label}</div>
                        {ex.perPerson && <div className="text-xs text-velta-mute">{guests} ospiti × {nights} notti</div>}
                      </div>
                      <div className="font-mono font-bold">{formatEUR(lineTotal)}</div>
                    </div>
                  );
                })}
                {customer.name && (
                  <div className="mt-3 pt-3 border-t border-dashed border-velta-border text-xs space-y-1">
                    <div><span className="text-velta-mute">Ospite:</span> <span className="font-medium">{customer.name}</span></div>
                    {customer.document_number && <div><span className="text-velta-mute">Doc:</span> <span className="font-mono">{customer.document_number}</span></div>}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-velta-mute text-sm p-8">Scegli una camera.</div>
            )}
          </div>
          <div className="border-t border-velta-border p-4 bg-velta-bg">
            <ScontrinoGestureLabel className="mb-1" />
            <GestionaleLongPress orderId={null} total={total} saveOrder={saveOrderForGestionale} onClosed={onGestionaleClosed}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale</span>
                <span data-testid="bnb-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(total)}</span>
              </div>
            </GestionaleLongPress>
            <PrecontoButton mode={mode} lines={activeRoom ? buildLines() : []} tenant={tenant} orderId={precontoOrderId} onOrderSaved={setPrecontoOrderId} testid="bnb-preconto-btn" />
            <button data-testid="bnb-pay-btn" disabled={!activeRoom || total <= 0} onClick={() => setShowPay(true)} className="w-full h-14 bg-[#0891B2] hover:bg-[#0E7490] text-white rounded-md font-medium text-lg flex items-center justify-center gap-2 btn-tactile disabled:opacity-40">
              <Receipt className="w-5 h-5" /> Scontrino
            </button>
          </div>
        </aside>
      </div>

      {showPay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowPay(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-semibold mb-4">Pagamento</h3>
            <div className="bg-velta-bg rounded-md p-4 mb-4 text-center"><div className="font-mono font-bold text-3xl">{formatEUR(total)}</div></div>
            <div className="mb-4"><LotteryCodeInput value={lotteryCode} onChange={setLotteryCode} testid="bnb-lottery" /></div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="bnb-pay-cash" onClick={() => close("cash")} className="h-14 bg-velta-ink text-white rounded-md font-medium btn-tactile">Contanti</button>
              <button data-testid="bnb-pay-card" onClick={() => close("card")} className="h-14 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Carta</button>
              <button data-testid="bnb-pay-ticket" onClick={() => close("ticket")} className="h-14 bg-velta-bg border border-velta-border text-velta-ink rounded-md font-medium btn-tactile">Ticket</button>
            </div>
          </div>
        </div>
      )}
      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
    </div>
  );
}
